import React from 'react';
import {
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import ProgressBar from '../../../../Shared/ProgressBar';
import { ReactComponent as HubIcon } from '../../../../../images/icons/assign_user.svg';
import {
  active,
  backgroundActive,
  inactive,
  lightBlack,
  lightGrey,
  lightTeal,
} from '../../../../../mainTheme';

const COLORS = {
  active: active,
  inactive: inactive,
  text: {
    primary: lightBlack,
    tertiary: lightGrey,
  },
  background: {
    active: backgroundActive,
    inactive: '#F6F6F9',
  },
} as const;

const RadioOption: React.FC<{
  value: string;
  title: string;
  isSelected: boolean;
}> = ({ value, title, isSelected }) => (
  <Box
    sx={{
      border: '1px solid',
      borderRadius: '8px',
      borderColor: isSelected ? COLORS.active : COLORS.inactive,
      p: 1,
      mb: 1,
      backgroundColor: isSelected
        ? COLORS.background.active
        : COLORS.background.inactive,
      height: '48px',
      alignItems: 'center',
      display: 'flex',
    }}
  >
    <FormControlLabel
      value={value}
      sx={{
        margin: 0,
        width: '100%',
        justifyContent: 'space-between',
        '& .MuiFormControlLabel-label': { flex: 1 },
      }}
      control={
        <Radio
          sx={{
            '&.MuiRadio-root': {
              color: isSelected ? lightTeal : COLORS.text.tertiary,
            },
          }}
        />
      }
      labelPlacement="start"
      label={
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.2px',
              ml: '8px',
              color: lightBlack,
            }}
          >
            {title}
          </Typography>
        </Box>
      }
    />
  </Box>
);

export const HubAssignmentConfirmation = ({
  onConfirm,
  onBack,
}: {
  onConfirm: (assign: boolean) => void;
  onBack: () => void;
}) => {
  const [wantHub, setWantHub] = React.useState('yes');
  const radioOptions = [
    {
      value: 'yes',
      title: 'Yes',
    },
    {
      value: 'no',
      title: 'No',
    },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <ProgressBar currentStep={2} totalSteps={2} />
      <HubIcon />
      <Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.2px',
            color: lightBlack,
          }}
        >
          Do you want to assign a Hub?
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '22px',
            letterSpacing: '0.2px',
            color: lightGrey,
          }}
        >
          This requires that you have the Hub serial number.
        </Typography>
      </Box>

      <RadioGroup value={wantHub} onChange={(e) => setWantHub(e.target.value)}>
        {radioOptions.map((option) => (
          <RadioOption
            key={option.value}
            {...option}
            isSelected={wantHub === option.value}
          />
        ))}
      </RadioGroup>

      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', mt: '16px' }}
      >
        <Button
          variant="text"
          color="secondary"
          onClick={onBack}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onConfirm(wantHub === 'yes')}
          sx={{
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '18px',
            letterSpacing: '0.2px',
            height: '44px',
            width: '180px',
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
